import { GoScreenFull } from "react-icons/go";
import {
  IoMdNotificationsOutline,
  IoMdNotifications,
  IoMdNotificationsOff,
} from "react-icons/io";
import { FiLogOut } from "react-icons/fi";
import { HiOutlineChatBubbleLeftRight } from "react-icons/hi2";
import { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosinstance";
import "./Header.css";
import API_URL from "../Config";
import { getMessaging, deleteToken } from "firebase/messaging";
import {
  app,
  registerFcmToken,
  isFcmRegistered,
  FCM_REGISTRATION_EVENT,
} from "../push/firebaseMessaging";
import { getAuthToken } from "../utils/authUtils";
import ClearIcon from "@mui/icons-material/Clear";
import { RiCheckDoubleFill } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import { GoArrowUpRight } from "react-icons/go";
import { getNotificationDotColor } from "../utils/notificationColors";
import { getNotificationRoute } from "../utils/notificationRoutes";
import {
  isTicketNotification,
  buildTicketNotificationOpenUrl,
} from "../utils/ticketNotificationOpen";

const getNotifPermission = () =>
  typeof Notification !== "undefined" && Notification.permission
    ? Notification.permission
    : "unsupported";

const parseNotificationCreatedAt = (createdAt) => {
  if (!createdAt) return 0;

  const value = String(createdAt).trim();
  const match = value.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:\s+[A-Za-z]{2,5})?$/,
  );

  if (match) {
    const [, day, month, year, hour = "0", minute = "0", second = "0"] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ).getTime();
  }

  const parsedTimestamp = Date.parse(value);
  return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
};

const formatRelativeNotificationTime = (createdAt, timezone) => {
  const withTimezone = (value) =>
    timezone ? `${value} ${String(timezone).trim()}` : value;

  const timestamp = parseNotificationCreatedAt(createdAt);
  const fallback = withTimezone(String(createdAt || "").trim()).trim();
  if (!timestamp) return fallback;

  const diffMs = Date.now() - timestamp;
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs < 0) return fallback;
  if (diffMs < minuteMs) return withTimezone("just now");
  if (diffMs < hourMs) return withTimezone(`${Math.floor(diffMs / minuteMs)}m ago`);
  if (diffMs < dayMs) return withTimezone(`${Math.floor(diffMs / hourMs)}h ago`);
  if (diffMs < 2 * dayMs) return withTimezone("yesterday");

  const date = new Date(timestamp);
  const isSameYear = date.getFullYear() === new Date().getFullYear();
  const formattedDate = new Intl.DateTimeFormat(
    "en-IN",
    isSameYear
      ? { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }
      : {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        },
  ).format(date);
  return withTimezone(formattedDate);
};

function Header({ isSidebarExpanded, onLogout }) {
  const [username, setUsername] = useState("");
  const [greeting, setGreeting] = useState("Hello");
  const navigate = useNavigate();
  const location = useLocation();
  const isGroupChatActive = location.pathname.startsWith("/group-chat");
  const notificationRef = useRef(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notifPermission, setNotifPermission] = useState(getNotifPermission());
  const [fcmRegistered, setFcmRegistered] = useState(isFcmRegistered());
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  const fetchChatUnreadCount = async () => {
    try {
      const response = await axiosInstance.get("/api/groups", {
        params: { page: 1, per_page: 100 },
      });
      const total = (response.data?.data || []).reduce(
        (sum, group) => sum + (Number(group.unread_count) || 0),
        0,
      );
      setChatUnreadCount(total);
    } catch (error) {
      console.error("Failed to fetch unread chat count:", error);
    }
  };

  useEffect(() => {
    const handleForegroundNotification = (event) => {
      const detail = event?.detail || {};
      const payload = detail?.rawPayload || detail;
      const incomingData = detail?.data || payload?.data || {};
      const notificationId = incomingData?.notification_id;

      if (["chat_message", "chat_group_invite"].includes(
        String(incomingData?.type || "").toLowerCase(),
      )) {
        fetchChatUnreadCount();
      }

      if (!notificationId) {
        return;
      }

      const title =
        detail?.title ||
        payload?.notification?.title ||
        incomingData?.title ||
        "New Notification";
      const body =
        detail?.body ||
        payload?.notification?.body ||
        incomingData?.body ||
        incomingData?.message ||
        "";

      const newNotification = {
        id: notificationId,
        title,
        message: body,
        is_read: false,
        created_at: incomingData?.created_at || new Date().toISOString(),
        timezone: incomingData?.timezone || "IST",
        type: incomingData?.type || "",
        reference_type: incomingData?.reference_type || "",
        reference_id: incomingData?.reference_id || "",
        equipment_id: incomingData?.equipment_id || "",
        process_id: incomingData?.process_id || "",
      };

      setNotifications((prev) => {
        if (prev.some((n) => String(n.id) === String(notificationId))) {
          return prev;
        }
        return [newNotification, ...prev];
      });
    };

    const handleServiceWorkerMessage = (event) => {
      if (event?.data?.type === "CHAT_MESSAGE_RECEIVED") {
        fetchChatUnreadCount();
        return;
      }
      if (event?.data?.type !== "PROCESS_UPDATE") return;

      const payload = event?.data?.payload || {};
      console.log("[FCM] PROCESS_UPDATE received from service worker:", payload);

      window.dispatchEvent(
        new CustomEvent("process-update-received", {
          detail: {
            equipment_id: payload.equipment_id,
            process_id: payload.process_id,
            status: payload.status,
            action: payload.action,
          },
        }),
      );
    };

    window.addEventListener(
      "foreground-notification-received",
      handleForegroundNotification,
    );
    navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);
    return () => {
      window.removeEventListener(
        "foreground-notification-received",
        handleForegroundNotification,
      );
      navigator.serviceWorker?.removeEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    };
  }, []);

  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await axiosInstance.get(`${API_URL}/api/notifications`);
      const serverNotifications = response.data?.notifications || [];

      setNotifications((prev) => {
        const serverIds = new Set(serverNotifications.map((n) => String(n.id)));
        const localOnly = prev.filter((n) => !serverIds.has(String(n.id)));
        return [...serverNotifications, ...localOnly];
      });
    } catch (error) {
      console.error(
        "Failed to fetch notifications:",
        error.response?.notifications || error.message,
      );
    } finally {
      setLoadingNotifications(false);
    }
  };

  const ViewAllNotifications = () => {
    navigate("/notifications");
    setShowNotifications(false);
  };

  const markNotificationRead = async (id) => {
    try {
      await axiosInstance.post(`/api/mark_read/${id}`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const toggleNotifications = () => {
    const permission = getNotifPermission();
    const registrationReady = isFcmRegistered();

    // If the browser has never asked yet, use the bell click as the explicit
    // user gesture that opens the setup explainer before requesting permission.
    if (permission === "default" && !registrationReady) {
      setShowNotifications(false);
      setShowEnableModal(true);
      return;
    }

    setShowNotifications((open) => {
      if (!open) fetchNotifications();
      return !open;
    });
  };

  const handleEnableNotifications = async () => {
    setShowEnableModal(false);
    setEnabling(true);
    try {
      const authKey = getAuthToken();
      await registerFcmToken(authKey);
    } catch (error) {
      console.error("Failed to enable notifications:", error);
    } finally {
      setNotifPermission(getNotifPermission());
      setFcmRegistered(isFcmRegistered());
      setEnabling(false);
    }
  };

  useEffect(() => {
    const recheck = () => {
      setNotifPermission(getNotifPermission());
      setFcmRegistered(isFcmRegistered());
    };
    const onRegistrationChange = (event) => {
      setFcmRegistered(Boolean(event?.detail?.registered ?? isFcmRegistered()));
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener(FCM_REGISTRATION_EVENT, onRegistrationChange);
    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener(FCM_REGISTRATION_EVENT, onRegistrationChange);
    };
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchChatUnreadCount();

    const handleChatReadUpdate = () => fetchChatUnreadCount();
    window.addEventListener("chatReadUpdated", handleChatReadUpdate);

    const handleNotificationUpdate = () => {
      fetchNotifications();
    };

    window.addEventListener("notificationUpdated", handleNotificationUpdate);

    function handleClickOutside(event) {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("chatReadUpdated", handleChatReadUpdate);
      window.removeEventListener("notificationUpdated", handleNotificationUpdate);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredNotifications = notifications.filter((n) => {
    if (activeTab === "read") return n.is_read;
    if (activeTab === "unread") return !n.is_read;
    return true;
  });

  const sortedFilteredNotifications = [...filteredNotifications].sort(
    (a, b) =>
      parseNotificationCreatedAt(b.created_at) -
      parseNotificationCreatedAt(a.created_at),
  );

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const fetchUserName = async () => {
    try {
      const response = await axiosInstance.get("/access/get_user_name");
      if (response.status === 200) {
        setGreeting(response.data.greet || "Hello");
        setUsername(response.data.user_name || "User");
      }
    } catch (error) {
      console.error(
        "Error fetching username:",
        error.response?.data || error.message,
      );
    }
  };

  useEffect(() => {
    fetchUserName();
  }, []);

  async function clearServiceWorkers() {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log("Service Worker unregistered:", registration.scope);
      }
    }
  }

  async function deleteFCMToken() {
    try {
      const messaging = getMessaging(app);
      const success = await deleteToken(messaging);

      if (success) {
        console.log("FCM token deleted.");
      } else {
        console.log("No FCM token to delete.");
      }
    } catch (error) {
      console.error("Error deleting FCM token:", error);
    }
  }

  async function clearDisplayedNotifications() {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      const visibleNotifications = await registration.getNotifications();
      visibleNotifications.forEach((notification) => notification.close());
    }
  }

  const handleLogout = async () => {
    await onLogout();
    await deleteFCMToken();
    await clearDisplayedNotifications();
    await clearServiceWorkers();
  };

  const handleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error(`Error attempting to exit fullscreen: ${err.message}`);
      });
    }
  };

  const navigateToReferenceToTicket = (notification) => {
    if (isTicketNotification(notification)) {
      navigate(buildTicketNotificationOpenUrl(notification));
      setShowNotifications(false);
      return;
    }

    const route = getNotificationRoute(notification);
    if (route) {
      navigate(route);
      setShowNotifications(false);
    }
  };

  const alertsActive = notifPermission === "granted" && fcmRegistered;
  const alertsBlocked = notifPermission === "denied";
  const alertsUnsupported = notifPermission === "unsupported";
  const alertsNeedSetup =
    !alertsActive && !alertsBlocked && !alertsUnsupported;

  let BellIcon = IoMdNotificationsOutline;
  let bellStateClass = "bell-active";
  let bellAriaLabel = "Notifications";

  if (alertsBlocked) {
    BellIcon = IoMdNotificationsOff;
    bellStateClass = "bell-blocked";
    bellAriaLabel =
      unreadCount > 0
        ? `Notifications blocked in browser. ${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`
        : "Notifications blocked in browser";
  } else if (alertsUnsupported) {
    BellIcon = IoMdNotificationsOff;
    bellStateClass = "bell-muted";
    bellAriaLabel = "Notifications unsupported in this browser";
  } else if (alertsNeedSetup) {
    BellIcon = IoMdNotificationsOff;
    bellStateClass = "bell-setup";
    bellAriaLabel =
      unreadCount > 0
        ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}. Alerts need setup`
        : "Alerts need setup";
  } else {
    BellIcon = unreadCount > 0 ? IoMdNotifications : IoMdNotificationsOutline;
    bellStateClass = unreadCount > 0 ? "bell-active bell-unread" : "bell-active";
    bellAriaLabel =
      unreadCount > 0
        ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`
        : "Notifications";
  }

  let notificationBanner = null;
  if (alertsBlocked) {
    notificationBanner = (
      <div className="notif-banner notif-banner-blocked" role="status" aria-live="polite">
        <div className="notif-banner-copy">
          <span>Browser notifications are blocked for this site.</span>
        </div>
      </div>
    );
  } else if (alertsUnsupported) {
    notificationBanner = (
      <div className="notif-banner notif-banner-muted" role="status" aria-live="polite">
        <div className="notif-banner-copy">
          <span>This browser does not support push notifications for this app.</span>
        </div>
      </div>
    );
  } else if (notifPermission === "granted" && !fcmRegistered) {
    notificationBanner = (
      <div className="notif-banner notif-banner-warn" role="status" aria-live="polite">
        <div className="notif-banner-copy">
          <span>Notifications are allowed, but this device is not fully registered yet.</span>
        </div>
        <button
          type="button"
          className="notif-banner-btn"
          onClick={handleEnableNotifications}
          disabled={enabling}
        >
          {enabling ? "Finishing..." : "Finish setup"}
        </button>
      </div>
    );
  } else if (notifPermission === "default") {
    notificationBanner = (
      <div className="notif-banner notif-banner-warn" role="status" aria-live="polite">
        <div className="notif-banner-copy">
          <span>Enable browser notifications to receive tickets and process updates.</span>
        </div>
        <button
          type="button"
          className="notif-banner-btn"
          onClick={() => setShowEnableModal(true)}
          disabled={enabling}
        >
          {enabling ? "Enabling..." : "Enable"}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`header-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="header-left">
        <div className="header-title-block">
          <h3 className="greeting">
            <span className="greeting-muted new-fs">{greeting}, </span>{" "}
            <span className="gradient-text new-fs">{username}</span>
          </h3>
        </div>
      </div>

      <div className="header-right">
        <button
          type="button"
          className="icon-btn-header"
          aria-label="Toggle full screen"
          onClick={handleFullScreen}
        >
          <GoScreenFull />
        </button>

         <button
          type="button"
          className={`icon-btn-header header-chat-btn${isGroupChatActive ? " active" : ""}`}
          aria-label={chatUnreadCount ? `Open group chat, ${chatUnreadCount} unread messages` : "Open group chat"}
          aria-current={isGroupChatActive ? "page" : undefined}
          title="Group Chat"
          onClick={() => navigate("/group-chat")}
        >
          <HiOutlineChatBubbleLeftRight />
          {chatUnreadCount > 0 && (
            <span className="header-chat-badge">
              {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
            </span>
          )}
        </button> 

        <div className="notification-bell-wrapper">
          <button
            type="button"
            className={`icon-btn-header notification-bell-btn ${bellStateClass}`}
            aria-label={bellAriaLabel}
            aria-haspopup="dialog"
            aria-expanded={showNotifications}
            onClick={toggleNotifications}
          >
            <BellIcon className="notification-bell-icon" />
            {unreadCount > 0 && <span className="notification-unread-dot" />}
          </button>

          {showNotifications && (
            <div
              ref={notificationRef}
              className="notification-panel"
              role="dialog"
              aria-label="Notifications panel"
            >
              <div className="notification-header">
                <h4>Notifications</h4>
                <button
                  type="button"
                  className="notification-close-btn"
                  aria-label="Close notifications panel"
                  onClick={() => setShowNotifications(false)}
                >
                  <ClearIcon fontSize="small" />
                </button>
              </div>

              {notificationBanner}

              <div
                className="notification-tabs"
                role="tablist"
                aria-label="Notification filters"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "all"}
                  className={activeTab === "all" ? "active" : ""}
                  onClick={() => setActiveTab("all")}
                >
                  All
                </button>

                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "read"}
                  className={activeTab === "read" ? "active" : ""}
                  onClick={() => setActiveTab("read")}
                >
                  Read
                </button>

                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "unread"}
                  className={activeTab === "unread" ? "active" : ""}
                  onClick={() => setActiveTab("unread")}
                >
                  Unread
                </button>
              </div>

              <div className="notification-list">
                {loadingNotifications && notifications.length === 0 ? (
                  <div className="notification-loading">
                    <span className="notification-spinner" />
                    Loading notifications...
                  </div>
                ) : sortedFilteredNotifications.length === 0 ? (
                  <div className="no-notifications">
                    No notifications
                  </div>
                ) : (
                  sortedFilteredNotifications.map((n) => {
                    const relativeTime = formatRelativeNotificationTime(
                      n.created_at,
                      n.timezone,
                    );
                    const messageText = n.message || n.content || n.body || "";
                    const titleText = n.title || messageText || "Notification";
                    const metadata = [
                      n.project_name && `Project: ${n.project_name}`,
                      n.equipment_number &&
                        `Equipment: ${n.equipment_number}`,
                      n.flange_number && `Flange: ${n.flange_number}`,
                      n.priority && `Priority: ${n.priority}`,
                      n.raised_by && `Raised by: ${n.raised_by}`,
                    ].filter(Boolean);

                    const openNotification = () => {
                      navigateToReferenceToTicket(n);
                    };

                    const handleNotificationKeyDown = (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openNotification();
                      }
                    };

                    return (
                      <div
                        key={n.id}
                        className={`notification-item${n.is_read ? " read" : " unread"}`}
                      >
                        <button
                          type="button"
                          className="notification-item-main"
                          onClick={openNotification}
                          onKeyDown={handleNotificationKeyDown}
                        >
                          <div className="notification-content">
                            <div className="notification-top-row">
                              <div className="notification-heading-wrap">
                                <span
                                  className="notification-item-dot"
                                  style={{
                                    backgroundColor: getNotificationDotColor(
                                      String(n.title || n.type || ""),
                                    ),
                                  }}
                                />
                                <div className="notification-message">{titleText}</div>
                              </div>
                            </div>

                            <div className="notification-created-at">
                              {relativeTime}
                            </div>

                            {messageText && messageText !== titleText && (
                              <div className="notification-title">{messageText}</div>
                            )}

                            {metadata.length > 0 && (
                              <div className="notification-desc notification-meta-details">
                                {metadata.join(" • ")}
                              </div>
                            )}
                          </div>
                        </button>

                        <div className="notification-actions">
                          {!n.is_read && (
                            <button
                              type="button"
                              className="notification-action-btn"
                              aria-label="Mark notification as read"
                              onClick={() => markNotificationRead(n.id)}
                            >
                              <RiCheckDoubleFill />
                            </button>
                          )}

                          <button
                            type="button"
                            className="notification-action-btn"
                            aria-label="Open notification"
                            onClick={openNotification}
                          >
                            <GoArrowUpRight />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <button
                type="button"
                onClick={ViewAllNotifications}
                className="view-all"
              >
                View All
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className="icon-btn-header"
          aria-label="Logout"
          onClick={handleLogout}
        >
          <FiLogOut />
        </button>
      </div>

      {showEnableModal && (
        <div
          className="notif-modal-overlay"
          onClick={() => setShowEnableModal(false)}
        >
          <div className="notif-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="notif-modal-title">Enable push notifications?</h3>
            <p className="notif-modal-text">
              You'll be alerted when tickets are assigned to you, processes are
              ready, and admin notes are added even when this tab is in the
              background. Your browser will ask for permission next.
            </p>
            <div className="notif-modal-actions">
              <button
                type="button"
                className="notif-modal-btn-secondary"
                onClick={() => setShowEnableModal(false)}
              >
                Not now
              </button>
              <button
                type="button"
                className="notif-modal-btn-primary"
                onClick={handleEnableNotifications}
                disabled={enabling}
              >
                {enabling ? "Enabling..." : "Enable"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Header;
